// src/lib/use-min-stay-default.js — Phase 6.B PR #167c (ADR-101)
//
// Hook léger pour gérer `logement_pricing_config.min_stay_default` côté UI :
//
//   - GET au mount : fetch /api/beds24-mapping?action=pricing_logement_config
//     pour récupérer la config + pricing_mode + min_stay_default actuels.
//   - Auto-sync au mount (PR #167c amend §Auto-sync) : POST
//     ?action=pricing_sync_min_stay_default_from_beds24 — lit Beds24 live +
//     UPSERT DB si drift détecté (PAS de push Beds24). Pattern Option B
//     READ-and-CACHE pour aligner DB FBZ ↔ Beds24 sans bouton manuel.
//   - save(value) : POST ?action=pricing_set_min_stay_default
//     pour push Beds24 priceRule[1].minimumStay + UPSERT DB.
//   - refreshBeds24() : GET ?action=pricing_get_min_stay_default_beds24
//     pour lire valeur live Beds24 + détecter drift (smoke S-2.bis garde 0→NULL).
//     Conservé pour usage bouton "🔄 Recharger Beds24" (lecture pure).
//
// Pattern partagé entre :
//   - Vue 1 fiche logement (SyncBeds24Tab → section "📐 Restrictions séjour")
//   - Vue 2 tableau Paramètres (Beds24SyncPanel → modal édition col 9)
//
// Cohérent BPRcv2-1 : composant UI fait son propre fetch GET (NE PAS s'appuyer
// sur prop `currentValue` depuis cfg V1 KV qui est toujours undefined).
//
// AbortController au unmount (cf. MPRc-5).
//
// PR #167c amend (§Auto-sync) — state `beds24SyncStatus` :
//   - 'idle' : pas encore tenté
//   - 'syncing' : fetch Beds24 en cours
//   - 'synced' : Beds24 == DB FBZ (cache valide)
//   - 'drift_corrected' : Beds24 != DB FBZ → DB UPSERT effectué silencieusement
//   - 'no_op' : pas de config FBZ pour ce logement (rien à sync)
//   - 'failed' : Beds24 unreachable / 5xx → user garde valeur cache DB
//
// L'auto-sync est non-bloquant : le rendu utilise d'abord la valeur DB
// (rapide), puis se met à jour async si Beds24 diverge.

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../supabase';

const LOGEMENT_ID_REGEX = /^[a-z0-9.]+$/i;

/**
 * Hook ciblé mono-logement pour min_stay_default.
 *
 * @param {string} logementId
 * @param {object} [options]
 * @param {boolean} [options.autoSync=true]
 *   Si true (défaut), déclenche fetchBeds24Live() au mount en arrière-plan.
 *   Mettre à false pour tests unitaires où l'on veut isoler le fetch initial.
 * @returns {{
 *   value: number|null,
 *   pricingMode: 'pricelabs'|'manual'|null,
 *   hasMapping: boolean,
 *   loading: boolean,
 *   error: string|null,
 *   driftDetected: boolean,
 *   beds24Raw: number|null,
 *   fetchedAt: string|null,
 *   beds24SyncStatus: 'idle'|'syncing'|'synced'|'drift_corrected'|'no_op'|'failed',
 *   refetch: () => Promise<void>,
 *   refreshBeds24: () => Promise<void>,
 *   save: (value: number|null) => Promise<{ok:boolean, error?:string, dryRun?:boolean}>,
 * }}
 */
export function useMinStayDefault(logementId, options = {}) {
  const { autoSync = true } = options;
  const [state, setState] = useState({
    value: null,
    pricingMode: null,
    hasMapping: false,
    loading: false,
    error: null,
    driftDetected: false,
    beds24Raw: null,
    fetchedAt: null,
    beds24SyncStatus: 'idle',
    // Marqueur "config fetch initial terminé" — évite que l'auto-sync se
    // déclenche avant que fetchConfig ait chargé hasMapping/pricingMode.
    configLoaded: false,
  });
  const mountedRef = useRef(true);
  // Anti double auto-sync (StrictMode + re-render). On ne déclenche qu'une
  // fois par logementId par instance.
  const autoSyncedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Reset le flag auto-sync si le logementId change (cas remount).
  useEffect(() => {
    autoSyncedRef.current = false;
  }, [logementId]);

  const fetchConfig = useCallback(async () => {
    if (!logementId || !LOGEMENT_ID_REGEX.test(logementId)) return;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) {
        if (!mountedRef.current) return;
        setState((s) => ({ ...s, loading: false, error: 'not_authenticated' }));
        return;
      }
      const res = await fetch(
        '/api/beds24-mapping?action=pricing_logement_config',
        {
          headers: { Authorization: `Bearer ${session.access_token}` },
        },
      );
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        if (!mountedRef.current) return;
        setState((s) => ({
          ...s,
          loading: false,
          error: json.error || `http_${res.status}`,
        }));
        return;
      }
      const json = await res.json();
      if (!mountedRef.current) return;
      const row = (json.configs || []).find((c) => c.logement_id === logementId);
      setState((s) => ({
        ...s,
        value: row?.min_stay_default ?? null,
        pricingMode: row?.pricing_mode ?? null,
        hasMapping: !!row?.beds24_room_id,
        loading: false,
        error: null,
        configLoaded: true,
      }));
    } catch (e) {
      if (!mountedRef.current) return;
      setState((s) => ({ ...s, loading: false, error: e.message || String(e) }));
    }
  }, [logementId]);

  // PR #167c amend (§Auto-sync) — fetchBeds24Live :
  // POST ?action=pricing_sync_min_stay_default_from_beds24
  //   → backend lit Beds24 + UPSERT DB silencieusement si drift.
  //   → on met à jour `value` + `beds24SyncStatus` selon la réponse.
  // Non-bloquant : si Beds24 fail, on garde la valeur DB cache + status 'failed'.
  const fetchBeds24Live = useCallback(async () => {
    if (!logementId || !LOGEMENT_ID_REGEX.test(logementId)) return;
    setState((s) => ({ ...s, beds24SyncStatus: 'syncing' }));
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) {
        if (!mountedRef.current) return;
        setState((s) => ({ ...s, beds24SyncStatus: 'failed' }));
        return;
      }
      const res = await fetch(
        '/api/beds24-mapping?action=pricing_sync_min_stay_default_from_beds24',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ logement_id: logementId }),
        },
      );
      if (!res.ok) {
        // Beds24 unreachable / 5xx → user garde valeur cache DB
        if (!mountedRef.current) return;
        setState((s) => ({ ...s, beds24SyncStatus: 'failed' }));
        return;
      }
      const json = await res.json();
      if (!mountedRef.current) return;
      const status = json.status; // 'synced' | 'drift_corrected' | 'no_op'
      const newValue = json.min_stay_default_fbz_after;
      setState((s) => ({
        ...s,
        // En cas de drift_corrected, on aligne value avec ce que la DB contient
        // post-UPSERT (= min_stay_default_fbz_after = valeur Beds24 normalisée).
        value: status === 'drift_corrected' ? (newValue ?? null) : s.value,
        beds24Raw: json.min_stay_beds24_raw ?? null,
        fetchedAt: json.fetched_at || null,
        beds24SyncStatus: status === 'no_op' ? 'no_op'
                          : status === 'drift_corrected' ? 'drift_corrected'
                          : 'synced',
        driftDetected: status === 'drift_corrected',
      }));
    } catch (_e) {
      // Silent : auto-sync est non-critique (UX fallback cache DB)
      if (!mountedRef.current) return;
      setState((s) => ({ ...s, beds24SyncStatus: 'failed' }));
    }
  }, [logementId]);

  // Mount : fetch DB FBZ d'abord (rapide) → on rend avec cache.
  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  // Mount : auto-sync Beds24 en arrière-plan (PR #167c amend §Auto-sync).
  // Démarre après le fetchConfig pour avoir un point de comparaison côté UI.
  // Évite de re-tirer si déjà fait pour ce logementId (autoSyncedRef).
  //
  // Garde-fou : on n'enclenche qu'après configLoaded=true (sinon hasMapping
  // est initialisé à false et le hook traite incorrectement "no_op").
  useEffect(() => {
    if (!autoSync) return;
    if (autoSyncedRef.current) return;
    if (!logementId || !LOGEMENT_ID_REGEX.test(logementId)) return;
    // Attend que fetchConfig ait terminé son cycle complet (configLoaded=true).
    if (!state.configLoaded) return;
    if (state.loading) return;
    if (state.error) return;
    // Si pas de mapping → pas de point de sync possible côté Beds24.
    if (!state.hasMapping) {
      autoSyncedRef.current = true;
      setState((s) => ({ ...s, beds24SyncStatus: 'no_op' }));
      return;
    }
    autoSyncedRef.current = true;
    fetchBeds24Live();
  }, [
    autoSync,
    logementId,
    state.configLoaded,
    state.loading,
    state.error,
    state.hasMapping,
    fetchBeds24Live,
  ]);

  const save = useCallback(
    async (newValue) => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session?.access_token) return { ok: false, error: 'not_authenticated' };
        const body = { logement_id: logementId, min_stay: newValue };
        const res = await fetch(
          '/api/beds24-mapping?action=pricing_set_min_stay_default',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${session.access_token}`,
            },
            body: JSON.stringify(body),
          },
        );
        const json = await res.json().catch(() => ({}));
        if (!res.ok) {
          return {
            ok: false,
            status: res.status,
            // 2026-09-15 : la PHRASE d'abord (un refus Beds24 porte son motif).
            error: json.message || json.error || `http_${res.status}`,
            verdict: json.verdict || null,
          };
        }
        if (mountedRef.current) {
          setState((s) => ({
            ...s,
            value: json.min_stay_default ?? null,
            // Save manuel push Beds24 → DB et Beds24 sont alignés après cette
            // action, on passe en 'synced' (UX cohérent).
            beds24SyncStatus: 'synced',
            driftDetected: false,
          }));
        }
        return { ok: true, dryRun: json.dry_run === true, value: json.min_stay_default };
      } catch (e) {
        return { ok: false, error: e.message || String(e) };
      }
    },
    [logementId],
  );

  const refreshBeds24 = useCallback(async () => {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) return;
      const qs = new URLSearchParams({
        action: 'pricing_get_min_stay_default_beds24',
        logement_id: logementId,
      });
      const res = await fetch(`/api/beds24-mapping?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (!res.ok) return;
      const json = await res.json();
      if (!mountedRef.current) return;
      setState((s) => ({
        ...s,
        beds24Raw: json.min_stay_beds24_raw ?? null,
        driftDetected: json.drift_detected === true,
        fetchedAt: json.fetched_at || null,
      }));
    } catch (_e) {
      // Silent : refreshBeds24 est non-critique (UX bouton info)
    }
  }, [logementId]);

  return {
    value: state.value,
    pricingMode: state.pricingMode,
    hasMapping: state.hasMapping,
    loading: state.loading,
    error: state.error,
    driftDetected: state.driftDetected,
    beds24Raw: state.beds24Raw,
    fetchedAt: state.fetchedAt,
    beds24SyncStatus: state.beds24SyncStatus,
    refetch: fetchConfig,
    refreshBeds24,
    save,
  };
}

// ── PR #167c amend C4 (§Auto-sync Vue 2) ────────────────────────────────
//
// Hook léger READ-ONLY pour drift detection per-ligne dans le tableau
// Beds24SyncPanel (Vue 2). NE fetch PAS la config (parent l'a déjà), juste
// déclenche l'auto-sync silencieux POST
// `pricing_sync_min_stay_default_from_beds24` au mount.
//
// Évite la complexité de fetchConfig + render gating de `useMinStayDefault`.
// Coût : 1 Beds24 fetch par row au mount du tableau (acceptable ~10-12 logs).
//
// Désactiver via `enabled=false` si parent veut différer (ex: row TYPE A
// pricelabs où l'admin n'éditera pas la valeur).
//
// PR #167c amend C10 (NQAc2-3 fix) — expose `clearDrift()` : permet au
// parent (MappingRow) de réinitialiser l'indicateur drift après un save
// modal réussi (où push Beds24 a aligné DB ↔ Beds24, drift devenu obsolète).
// Sans clearDrift, le state local `beds24SyncStatus='drift_corrected'` reste
// affiché jusqu'au remount complet du tableau (refresh page).
//
// @param {string} logementId
// @param {boolean} [enabled=true]
//   Si false, ne déclenche jamais le fetch (utile pour lignes pricelabs).
//
// @returns {{ beds24SyncStatus, beds24Raw, fetchedAt, dbValueAfter, clearDrift }}
//
export function useMinStayDefaultSync(logementId, enabled = true) {
  const [state, setState] = useState({
    beds24SyncStatus: 'idle',
    beds24Raw: null,
    fetchedAt: null,
    dbValueAfter: null,
  });
  const mountedRef = useRef(true);
  const firedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    firedRef.current = false;
  }, [logementId]);

  useEffect(() => {
    if (!enabled) return;
    if (firedRef.current) return;
    if (!logementId || !LOGEMENT_ID_REGEX.test(logementId)) return;
    firedRef.current = true;
    (async () => {
      setState((s) => ({ ...s, beds24SyncStatus: 'syncing' }));
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session?.access_token) {
          if (!mountedRef.current) return;
          setState((s) => ({ ...s, beds24SyncStatus: 'failed' }));
          return;
        }
        const res = await fetch(
          '/api/beds24-mapping?action=pricing_sync_min_stay_default_from_beds24',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({ logement_id: logementId }),
          },
        );
        if (!res.ok) {
          if (!mountedRef.current) return;
          setState((s) => ({ ...s, beds24SyncStatus: 'failed' }));
          return;
        }
        const json = await res.json();
        if (!mountedRef.current) return;
        const status = json.status;
        setState({
          beds24SyncStatus:
            status === 'no_op' ? 'no_op'
            : status === 'drift_corrected' ? 'drift_corrected'
            : 'synced',
          beds24Raw: json.min_stay_beds24_raw ?? null,
          fetchedAt: json.fetched_at || null,
          dbValueAfter: json.min_stay_default_fbz_after ?? null,
        });
      } catch (_e) {
        if (!mountedRef.current) return;
        setState((s) => ({ ...s, beds24SyncStatus: 'failed' }));
      }
    })();
  }, [enabled, logementId]);

  // PR #167c amend C10 (NQAc2-3 fix) — clearDrift() reset state local :
  // - beds24SyncStatus → 'idle' (cache le pill ⚠️ amber dans MappingRow)
  // - firedRef → false (permet à un futur re-mount/re-render de re-fetch
  //   Beds24 si parent change `enabled` ou si MappingRow se ré-affiche)
  // Use case : appelé par MappingRow quand `config.min_stay_default` change
  // (= post-save modal + refetchConfigs Vue 2), car le push manuel a aligné
  // DB ↔ Beds24 → le drift précédemment détecté n'est plus pertinent.
  const clearDrift = useCallback(() => {
    firedRef.current = false;
    setState({
      beds24SyncStatus: 'idle',
      beds24Raw: null,
      fetchedAt: null,
      dbValueAfter: null,
    });
  }, []);

  return { ...state, clearDrift };
}
